import { useContext, useEffect, useState } from "react";
import { AppError } from "../../../../stores/models/errors/AppError";
import { IRegistrationCredentialsFormValues } from "../../../authentication/_interfaces/IRegistrationCredentialsFormValues";
import { IOrganizationSignUpFlowValues } from "../_interfaces/IOrganizationSignUpFlowValues";
import { AccountDetailsStepValues } from "../_interfaces/IOrganizationSignUpStepTypeValues";
import { AccountRegistrationValidation } from "../validation/AccountRegistrationValidation";
import { RootContext, UserType } from "../../../../stores";
import { StubUser } from "../../../../stores/models/StubUser";
import { OrganizationType } from "../../../../stores/models/StubOrganization";
import { AppErrors } from "../../../../stores/models/constants/AppErrors";

export function useAccountDetailsController(
    values: AccountDetailsStepValues,
    allValues: IOrganizationSignUpFlowValues,
    update: (patch: Partial<AccountDetailsStepValues>) => void,
    onSuccess: (override?: Partial<AccountDetailsStepValues>) => void
): {
    values: IRegistrationCredentialsFormValues,
    onChange: (patch: Partial<IRegistrationCredentialsFormValues>) => void,
    onSubmit: (override?: Partial<AccountDetailsStepValues>) => void,
    isSubmitting: boolean,
    error: string | undefined,
    fieldErrors: Partial<Record<keyof IRegistrationCredentialsFormValues, string>>
} {
    const rootStore = useContext(RootContext);

    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof IRegistrationCredentialsFormValues, string>>>({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);

    useEffect(() => {
        update({ backClicked: false, password: "", confirmedPassword: "" })
    }, [])

    useEffect(() => {
        update({ passwordsMatch: values.password === values.confirmedPassword });
    }, [values.password, values.confirmedPassword])

    useEffect(() => {
        if (validationRun) {
            const errors = validate().errors;
            setFieldErrors(errors);
        }
    }, [values.username, values.password, values.confirmedPassword, values.termsOfServiceAccepted, validationRun])


    const validate = (override?: Partial<AccountDetailsStepValues>) => {
        return AccountRegistrationValidation({ ...values, ...override });
    }

    const isValidSubmission = (override?: Partial<AccountDetailsStepValues>) => {
        return validate(override).valid;
    }

    const submit = async (override?: Partial<AccountDetailsStepValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);

        try {
            const isBackRequest = allValues.backClicked || override?.backClicked;
            const isLoginRequest = allValues.hasAccount || override?.hasAccount;
            const shouldSwitchStepsWithoutValidation = isBackRequest || isLoginRequest;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission(override)) {
                update({ loading: true });
                const newUser = new StubUser(UserType.Organization);
                newUser.setTermsOfServiceAccepted(values.termsOfServiceAccepted);
                newUser.credentials.setEmail(values.username);
                newUser.credentials.setPassword(values.password);
                newUser.stubOrganization?.setOrganizationType(allValues.isForProfit ? OrganizationType.Forprofit : OrganizationType.Nonprofit);
                if (allValues.organizationName && allValues.ein && allValues.signUpCode && allValues.mission && allValues.description) {
                    newUser.stubOrganization?.setName(allValues.organizationName);
                    newUser.stubOrganization?.setEIN(allValues.ein);
                    newUser.stubOrganization?.setSignUpCode(allValues.signUpCode);
                    newUser.stubOrganization?.setMission(allValues.mission);
                    newUser.stubOrganization?.setDescription(allValues.description);
                    newUser.stubOrganization?.setWebsite(allValues.website || "");
                } else {
                    throw "Missing property"
                }
                const result = await rootStore.registrationStore.register(newUser);
                update({ ...override, loading: false });
                onSuccess(override);
            }
        } catch (err) {
            if (err instanceof AppError) {
                switch (err.code) {
                    case AppErrors.MATCHING_RECORD_EXISTS.code:
                        setError("That email is already associated with an account. Did you mean to login?")
                        break;
                    case AppErrors.CODE_INVALID.code:
                        setError("The supplied sign up code is invalid.")
                        break;
                    case AppErrors.CODE_USED.code:
                        setError("The supplied sign up code has already been used.")
                        break;
                    case AppErrors.CODE_EXPIRED.code:
                        setError("The supplied sign up code has expired. Please request a new one.")
                        break;
                    default:
                        setError(err.message);
                }
            } else {
                setError("An unknown error occurred.");
            }
        } finally {
            update({ loading: false });
            setSubmitting(false);
        }
    };

    return {
        values,
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}